// ========================================
// EMAIL SERVICE (SendGrid)
// ========================================
// src/services/email/emailService.ts

import apiClient from '@/api/apiClient';

export interface EmailTemplate {
    PAYMENT_SUCCESS: string;
    PAYMENT_FAILED: string;
    BANK_TRANSFER_INSTRUCTIONS: string;
    LICENSE_ACTIVATED: string;
    LICENSE_EXPIRING: string;
    INVOICE: string;
}

export interface EmailData {
    [key: string]: any;
}

class EmailService {

    /**
     * Send template email
     * Backend will handle SendGrid integration
     */
    async sendTemplateEmail(
        to: string,
        template: keyof EmailTemplate,
        data: EmailData
    ): Promise<void> {
        try {
            console.log(`📧 Sending ${template} email to:`, to);

            await apiClient.post('/api/emails/send-template', {
                to,
                template,
                data
            });

            console.log('✅ Email sent successfully');
        } catch (error: any) {
            console.error('❌ Failed to send email:', error);
            // Don't throw - email failure shouldn't break main flow
        }
    }

    /**
     * Send custom email
     */
    async sendEmail(params: {
        to: string;
        subject: string;
        html: string;
        attachments?: Array<{
            filename: string;
            content: Buffer | string;
            contentType?: string;
        }>;
    }): Promise<void> {
        try {
            console.log('📧 Sending custom email to:', params.to);

            await apiClient.post('/api/emails/send', params);

            console.log('✅ Email sent successfully');
        } catch (error: any) {
            console.error('❌ Failed to send email:', error);
        }
    }

    // ========================================
    // SPECIFIC EMAIL METHODS
    // ========================================

    async sendPaymentSuccessEmail(
        to: string,
        data: {
            buyerName: string;
            paymentId: string;
            amount: string;
            contentTitle: string;
        }
    ): Promise<void> {
        return this.sendTemplateEmail(to, 'PAYMENT_SUCCESS', data);
    }

    async sendLicenseActivatedEmail(
        to: string,
        data: {
            buyerName: string;
            licenseNumber: string;
            contentTitle: string;
            downloadLink: string;
            expiryDate: string;
        }
    ): Promise<void> {
        return this.sendTemplateEmail(to, 'LICENSE_ACTIVATED', data);
    }

    async sendLicenseExpiringEmail(
        to: string,
        data: {
            buyerName: string;
            licenseNumber: string;
            contentTitle: string;
            daysLeft: number;
            renewalLink: string;
        }
    ): Promise<void> {
        return this.sendTemplateEmail(to, 'LICENSE_EXPIRING', data);
    }

    async sendInvoiceEmail(
        to: string,
        data: {
            invoiceNumber: string;
            invoiceDate: string;
            total: string;
            downloadLink: string;
        }
    ): Promise<void> {
        return this.sendTemplateEmail(to, 'INVOICE', data);
    }
}

export const emailService = new EmailService();
export default emailService;
